/**
 * CourseCreator - Interactive map tool for defining race courses (start line, finish line, marks).
 * Uses MapLibre draggable markers and deck.gl overlay for rendering course elements.
 * Produces a Course object consumed by the RaceTracks ranking engine.
 */
import maplibregl from 'maplibre-gl';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { PathLayer, TextLayer, ScatterplotLayer } from '@deck.gl/layers';
import { Info } from '../domain/Info.js';
import * as geom from '../utils/geometry-util.js';
import { along, lineString } from '@turf/turf';
import * as util from '../utils/utils.js';

const DEBOUNCE_MS = 1500;

const COLORS = {
  startLine: [0, 180, 0, 255],
  startLineText: [0, 180, 0, 100],
  finishLine: [200, 0, 0, 255],
  finishLineText: [200, 0, 0, 100],
  mark: [255, 140, 0, 255],
  markText: [0, 0, 0, 100],
  textOutline: [255, 255, 255, 255]
};

const MARKER_COLORS = {
  startLine: '#00b400',
  finishLine: '#c80000',
  mark: '#ff8c00'
};

export class CourseCreator {
  constructor(map, info, onInfoChanged, markerFactory = null) {
    this._map = map;
    this._info = info;
    this._onInfoChanged = onInfoChanged;
    this._markerFactory = markerFactory || ((opts) => new maplibregl.Marker(opts));
    this._trackAnimator = null;
    this._activePlacementMode = 'start';
    this._markers = new Map();
    this._overlay = null;
    this._selectedMarkerKey = null;
    this._debounceTimeout = null;
    this._active = false;
    this._clickHandler = null;
    this._keyHandler = null;
  }

  activate() {
    if (this._active) return;
    this._active = true;
    this._activePlacementMode = 'start';

    this._ensureOverlay();

    this._clickHandler = (e) => this._handleMapClick(e);
    this._map.on('click', this._clickHandler);

    this._keyHandler = (e) => this._handleKeyDown(e);
    document.addEventListener('keydown', this._keyHandler);

    this._setMarkersInteractive(true);
    this._showDrawer(true);
    this._updateModeButtons();
    this._setupStartTimeButton();
    this._setupDeleteButton();
    this._setupUIEventHandlers();
    this._redrawOverlay();
  }

  deactivate() {
    if (!this._active) return;
    this._active = false;

    if (this._clickHandler) {
      this._map.off('click', this._clickHandler);
      this._clickHandler = null;
    }
    if (this._keyHandler) {
      document.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }

    this._setMarkersInteractive(false);
    this._showDrawer(false);
  }

  setInfo(info) {
    this._info = info;
  }

  setTrackAnimator(trackAnimator) {
    this._trackAnimator = trackAnimator;
  }

  isActive() {
    return this._active;
  }

  getActivePlacementMode() {
    return this._activePlacementMode;
  }

  setPlacementMode(mode) {
    this._activePlacementMode = mode;
    this._updateModeButtons();
  }



  loadCourse() {
    this._clearAllMarkers();
    
    const startLine = this._info.getStartLine();
    if (startLine && startLine[0]) {
      this._addMarkerForPoint('start-p1', startLine[0], MARKER_COLORS.startLine);
      if (startLine[1]) this._addMarkerForPoint('start-p2', startLine[1], MARKER_COLORS.startLine);
    }

    const finishLine = this._info.getFinishLinePoints();
    if (finishLine && finishLine[0]) {
      this._addMarkerForPoint('finish-p1', finishLine[0], MARKER_COLORS.finishLine);
      if (finishLine[1]) this._addMarkerForPoint('finish-p2', finishLine[1], MARKER_COLORS.finishLine);
    }

    const marks = this._info.getMarks();
    for (const m of marks) {
      this._addMarkerForPoint(`mark-${m.id}`, { lat: m.lat, lon: m.lon }, MARKER_COLORS.mark);
    }

    this._updateMarkListField();
    this._updateStartTimeDisplay();

    const startTime = this._info.getStartTime();
    if (startTime && this._trackAnimator) {
      const startMoment = typeof startTime === 'string' 
        ? new Date(startTime).getTime()
        : startTime;
      this._trackAnimator.setCurrentPlayTime(startMoment);
    }

    this._ensureOverlay();
    this._redrawOverlay();
  }

  displayCourse() {
    this._setMarkersInteractive(false);
    this._ensureOverlay();
    this._redrawOverlay();
  }

  _hideMarkers() {
    for (const marker of this._markers.values()) {
      marker.getElement().style.display = 'none';
    }
  }

  _showMarkers() {
    for (const marker of this._markers.values()) {
      marker.getElement().style.display = 'block';
    }
  }

  _ensureOverlay() {
    if (!this._overlay) {
      this._overlay = new MapboxOverlay({ layers: [] });
      this._map.addControl(this._overlay);
    }
  }

  _handleMapClick(e) {
    const coord = { lat: e.lngLat.lat, lon: e.lngLat.lng };

    this._selectedMarkerKey = null;
    this._updateHighlights();

    if (this._activePlacementMode === 'start') {
      this._placeStartLinePoint(coord);
    } else if (this._activePlacementMode === 'marks') {
      this._placeMark(coord);
    } else if (this._activePlacementMode === 'finish') {
      this._placeFinishLinePoint(coord);
    }
  }

  _placeStartLinePoint(coord) {
    const startLine = this._info.getStartLine();
    if (!startLine || !startLine[0]) {
      this._info.setStartLine(coord, null);
      this._addMarkerForPoint('start-p1', coord, MARKER_COLORS.startLine);
    } else if (!startLine[1]) {
      this._info.setStartLine(startLine[0], coord);
      this._addMarkerForPoint('start-p2', coord, MARKER_COLORS.startLine);
      this._activePlacementMode = 'marks';
      this._updateModeButtons();
    }
    this._redrawOverlay();
    this._scheduleSave();
  }

  _placeFinishLinePoint(coord) {
    const finishLine = this._info.getFinishLinePoints();
    if (!finishLine || !finishLine[0]) {
      this._removeMarker('finish-p1');
      this._removeMarker('finish-p2');
      this._info.setFinishLine(coord, null);
      this._addMarkerForPoint('finish-p1', coord, MARKER_COLORS.finishLine);
    } else if (!finishLine[1]) {
      this._info.setFinishLine(finishLine[0], coord);
      this._addMarkerForPoint('finish-p2', coord, MARKER_COLORS.finishLine);
    }
    this._redrawOverlay();
    this._scheduleSave();
  }

  _placeMark(coord) {
    const id = this._info.getNextMarkId();
    this._info.addMark(id, coord.lat, coord.lon);
    this._addMarkerForPoint(`mark-${id}`, coord, MARKER_COLORS.mark);
    const existing = this._info.getMarkList();
    const newEntry = `${id}`;
    this._info.setMarkList(existing ? `${existing} ${newEntry}` : newEntry);
    this._updateMarkListField();
    this._redrawOverlay();
    this._scheduleSave();
  }

  _createDotElement(color) {
    const el = document.createElement('div');
    el.style.width = '12px';
    el.style.height = '12px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = 'transparent';
    el.style.border = 'none';
    el.style.cursor = 'pointer';
    return el;
  }


  _updateHighlights() {
    this._redrawOverlay();
  }

  _addMarkerForPoint(key, coord, color) {
    const el = this._createDotElement(color);
    const marker = this._markerFactory({ element: el, draggable: this._active })
      .setLngLat([coord.lon, coord.lat])
      .addTo(this._map);

    marker.on('dragend', () => {
      const lngLat = marker.getLngLat();
      this._updateCoordFromMarker(key, lngLat.lat, lngLat.lng);
      this._redrawOverlay();
      this._scheduleSave();
    });

    marker.getElement().addEventListener('click', (e) => {
      e.stopPropagation();
      this._selectedMarkerKey = key;
      this._updateHighlights();
    });

    this._markers.set(key, marker);
  }

  _updateCoordFromMarker(key, lat, lon) {
    if (key === 'start-p1') {
      const startLine = this._info.getStartLine();
      this._info.setStartLine({ lat, lon }, startLine ? startLine[1] : null);
    } else if (key === 'start-p2') {
      const startLine = this._info.getStartLine();
      this._info.setStartLine(startLine ? startLine[0] : null, { lat, lon });
    } else if (key === 'finish-p1') {
      const finishLine = this._info.getFinishLinePoints();
      this._info.setFinishLine({ lat, lon }, finishLine ? finishLine[1] : null);
    } else if (key === 'finish-p2') {
      const finishLine = this._info.getFinishLinePoints();
      this._info.setFinishLine(finishLine ? finishLine[0] : null, { lat, lon });
    } else if (key.startsWith('mark-')) {
      const id = parseInt(key.split('-')[1]);
      this._info.updateMark(id, lat, lon);
    }
  }

  _handleKeyDown(e) {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      this._deleteSelected();
    }
  }

  _deleteSelected() {
    if (!this._selectedMarkerKey) return;
    const key = this._selectedMarkerKey;

    if (key === 'start-p1' || key === 'start-p2') {
      this._removeMarker('start-p1');
      this._removeMarker('start-p2');
      this._info.clearStartLine();
    } else if (key === 'finish-p1' || key === 'finish-p2') {
      this._removeMarker('finish-p1');
      this._removeMarker('finish-p2');
      this._info.clearFinishLine();
    } else if (key.startsWith('mark-')) {
      const id = parseInt(key.split('-')[1]);
      this._removeMarker(key);
      this._info.deleteMark(id);
      this._info.removeMarkFromList(id);
      this._updateMarkListField();
    }

    this._selectedMarkerKey = null;
    this._updateHighlights();
    this._redrawOverlay();
    this._scheduleSave();
  }

  _removeMarker(key) {
    const marker = this._markers.get(key);
    if (marker) {
      marker.remove();
      this._markers.delete(key);
    }
  }

  updateMarkListFromText(text) {
    const parsed = this._info.parseMarksList(text);
    if (parsed.error) return parsed;
    this._info.setMarkList(text);
    this._redrawOverlay();
    this._scheduleSave();
    return parsed;
  }

  _scheduleSave() {
    if (this._debounceTimeout) clearTimeout(this._debounceTimeout);
    this._debounceTimeout = setTimeout(() => {
      this._debounceTimeout = null;
      const errors = this._info.validateCourse();
      if (errors.length > 0) {
        util.showToast('Course incomplete: ' + errors.join(', '));
        return;
      }
      if (this._onInfoChanged) {
        this._onInfoChanged(this._info);
      }
    }, DEBOUNCE_MS);
  }

  _setMarkersInteractive(draggable) {
    for (const marker of this._markers.values()) {
      marker.setDraggable(draggable);
    }
  }

  clearCourse() {
    this._clearAll();
    if (this._overlay) {
      this._overlay.setProps({ layers: [] });
    }
  }

  _clearAllMarkers() {
    for (const marker of this._markers.values()) {
      marker.remove();
    }
    this._markers.clear();
    this._selectedMarkerKey = null;
  }

  _clearAll() {
    this._clearAllMarkers();
    this._info.clearCourse();
    this._updateStartTimeDisplay();
  }

  _showDrawer(visible) {
    const drawer = document.getElementById('courseCreatorDrawer');
    if (drawer) drawer.style.display = visible ? 'flex' : 'none';
  }

  _updateModeButtons() {
    (document.getElementById('btnCourseStart')).classList.toggle('selected', this._activePlacementMode === 'start');
    (document.getElementById('btnCourseMarks')).classList.toggle('selected', this._activePlacementMode === 'marks');
    (document.getElementById('btnCourseFinish')).classList.toggle('selected', this._activePlacementMode === 'finish');
  }

  _updateMarkListField() {
    const field = document.getElementById('courseMarkList');
    if (field) field.value = this._info.getMarkList();
  }

  _setupStartTimeButton() {
    const btn = document.getElementById('btnCourseStartTime');
    if (!btn) return;
    btn.onclick = () => {
      this._setStartTimeFromAnimator();
      util.showToast("Set start time")
    };
  }

  _setupDeleteButton() {
    (document.getElementById('btnDeleteCourse')).onclick = () => this._showDeleteConfirmation();
    (document.getElementById('deleteCourseConfirmButton')).onclick = () => this._confirmDeleteCourse();
    (document.getElementById('deleteCourseCancel')).onclick = () => this._hideDeleteConfirmation();
  }

  _setupUIEventHandlers() {
    (document.getElementById('btnCourseStart')).addEventListener('click', () => {
      this.setPlacementMode('start');
      util.showToast("Click two points to set start line, drag to move, select and hit delete to remove");

    });
    (document.getElementById('btnCourseMarks')).addEventListener('click', () => {
      this.setPlacementMode('marks')
      util.showToast("Click on map to set marks, drag to move, select and hit delete to remove");
    });
    (document.getElementById('btnCourseFinish')).addEventListener('click', () => {
      this.setPlacementMode('finish');
      util.showToast("Create a separate finish line (if different from start)");
    });

    const markList = document.getElementById('courseMarkList');
    if (markList) {
      markList.addEventListener('focus', () => {
        this.setPlacementMode(null);
        util.showToast("Enter course list as mark numbers");
      });
      markList.addEventListener('change', (e) => this.updateMarkListFromText(e.target.value));
    }
  }

  _showDeleteConfirmation() {
    const dialog = document.getElementById('deleteCourseDialog');
    if (dialog) dialog.style.display = 'flex';
  }

  _hideDeleteConfirmation() {
    const dialog = document.getElementById('deleteCourseDialog');
    if (dialog) dialog.style.display = 'none';
  }

  _confirmDeleteCourse() {
    this._hideDeleteConfirmation();
    this.clearCourse();
    this._updateMarkListField();
    this._redrawOverlay();
    if (this._onInfoChanged) {
      this._onInfoChanged(this._info);
    }
    util.showToast('Course deleted');
  }

  _setStartTimeFromAnimator() {
    if (!this._trackAnimator) return;
    const currentTime = this._trackAnimator.getCurrentTime();
    if (currentTime) {
      this._info.setStartTime(currentTime.toISOString());
      this._updateStartTimeDisplay();
      this._scheduleSave();
    }
  }

  _updateStartTimeDisplay() {
    if (typeof document === 'undefined') return;
    const display = document.getElementById('courseStartTime');
    if (!display) return;
    const startTime = this._info.getStartTime();
    if (startTime) {
      const date = new Date(startTime);
      display.textContent = date.toLocaleTimeString();
    } else {
      display.textContent = '';
    }
  }

  _redrawOverlay() {
    if (!this._overlay) return;

    const layers = [];
    const paths = [];
    const labels = [];
    const boundaryLines = [];
    const markerDots = [];

    const startLine = this._info.getStartLine();
    if (startLine && startLine[0] && startLine[1]) {
      paths.push({
        path: [[startLine[0].lon, startLine[0].lat],
               [startLine[1].lon, startLine[1].lat]],
        color: COLORS.startLine
      });
      const mid = geom.lineMidpoint(startLine);
      labels.push({ position: [mid.lon, mid.lat], text: 'Start', color: COLORS.startLineText });
      
      const isStartSelected = this._selectedMarkerKey === 'start-p1' || this._selectedMarkerKey === 'start-p2';
      markerDots.push({ 
        position: [startLine[0].lon, startLine[0].lat], 
        color: COLORS.startLine,
        highlighted: isStartSelected
      });
      markerDots.push({ 
        position: [startLine[1].lon, startLine[1].lat], 
        color: COLORS.startLine,
        highlighted: isStartSelected
      });
    }

    const finishLine = this._info.getFinishLinePoints();
    if (finishLine && finishLine[0] && finishLine[1]) {
      paths.push({
        path: [[finishLine[0].lon, finishLine[0].lat], [finishLine[1].lon, finishLine[1].lat]],
        color: COLORS.finishLine
      });
      const mid = geom.lineMidpoint(finishLine);
      labels.push({ position: [mid.lon, mid.lat], text: 'Finish', color: COLORS.finishLineText });
      
      const isFinishSelected = this._selectedMarkerKey === 'finish-p1' || this._selectedMarkerKey === 'finish-p2';
      markerDots.push({ 
        position: [finishLine[0].lon, finishLine[0].lat], 
        color: COLORS.finishLine,
        highlighted: isFinishSelected
      });
      markerDots.push({ 
        position: [finishLine[1].lon, finishLine[1].lat], 
        color: COLORS.finishLine,
        highlighted: isFinishSelected
      });
    }

    const marks = this._info.getMarks();
    const parsed = this._info ? this._info.parseMarksList() : null;
    const courseLegs = this._info ? this._info.calcCourseLegs() : null;

    for (const m of marks) {
      const labelText =  `${m.id}`;
      labels.push({ position: [m.lon, m.lat], text: labelText, color: COLORS.markText });
      
      const isMarkSelected = this._selectedMarkerKey === `mark-${m.id}`;
      markerDots.push({ 
        position: [m.lon, m.lat], 
        color: COLORS.mark,
        highlighted: isMarkSelected
      });
    }

    const markCircles = [];
    if (courseLegs && courseLegs.length > 0) {
      for (let i = 0; i < courseLegs.length; i++) {
        const leg = courseLegs[i];
        
        if (leg.start && leg.end) {
          paths.push({
            path: [[leg.start.lon, leg.start.lat], [leg.end.lon, leg.end.lat]],
            color: [150, 150, 150, 50] 
          });
        }
        
        if (i < courseLegs.length - 1 && leg.end) {
          markCircles.push({
            position: [leg.end.lon, leg.end.lat],
            radius: Info.FUZZY_MARK_RADIUS_M,
            color: [100, 150, 255, 20]
          });
        }
      }
    }

    const overlayLayers = [];

    if (paths.length > 0) {
      overlayLayers.push(new PathLayer({
        id: 'course-lines',
        data: paths,
        getPath: d => d.path,
        getColor: d => d.color,
        getWidth: 3,
        widthMinPixels: 2
      }));
    }

    if (markCircles.length > 0) {
      overlayLayers.push(new ScatterplotLayer({
        id: 'mark-circles',
        data: markCircles,
        getPosition: d => d.position,
        getFillColor: d => d.color,
        getRadius: d => d.radius,
        radiusScale: 1,
        radiusMinPixels: 2,
        stroked: false,
        pickable: false
      }));
    }

    if (boundaryLines.length > 0) {
      overlayLayers.push(new PathLayer({
        id: 'course-boundaries',
        data: boundaryLines,
        getPath: d => d.path,
        getColor: d => d.color,
        getWidth: 2,
        widthMinPixels: 1,
        getDashArray: [5, 3]
      }));
    }

    if (markerDots.length > 0) {
      overlayLayers.push(new ScatterplotLayer({
        id: 'course-marker-dots',
        data: markerDots,
        getPosition: d => d.position,
        getFillColor: d => d.color,
        getRadius: d => d.highlighted ? 8 : 6,
        radiusMinPixels: 6,
        radiusMaxPixels: 8,
        stroked: true,
        getLineColor: d => d.highlighted ? [255, 255, 0, 255] : [255, 255, 255, 255],
        getLineWidth: d => d.highlighted ? 3 : 2,
        lineWidthMinPixels: 2,
        lineWidthMaxPixels: 3
      }));
    }

    if (labels.length > 0) {
      overlayLayers.push(new TextLayer({
        id: `course-labels-${labels.length}`,
        data: labels,
        getPosition: d => d.position,
        getText: d => d.text,
        getSize: 14,
        getColor: d => d.color,
        outlineColor: COLORS.textOutline,
        outlineWidth: 3,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'bottom',
        getPixelOffset: [0, -15]
      }));
    }

    this._overlay.setProps({ layers: overlayLayers });
  }



  destroy() {
    this.deactivate();
    this.clearCourse();
    if (this._debounceTimeout) clearTimeout(this._debounceTimeout);
    for (const marker of this._markers.values()) {
      marker.remove();
    }
    this._markers.clear();
    if (this._overlay && this._map) {
      this._map.removeControl(this._overlay);
      this._overlay = null;
    }
  }
}
